import { useMemo, useState } from 'react';

import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import {
  ActivityOutcomeGroup,
  ActivityOutcomes,
} from '../key-activity-information/activity-outcomes.constants';

type Props = {
  outcomes: ActivityOutcomes;
};

const useTeachingNotesOutcomeDisplay = ({ outcomes }: Props) => {
  const [activeCurriculum, setActiveCurriculum] = useState<string>('');

  const { data } = useGetStartupData();

  const curriculumOptions = useMemo(() => {
    return (
      data?.startupData.curriculums.map(c => ({
        label: c.name,
        value: c.name,
      })) || []
    );
  }, [data?.startupData.curriculums]);

  // Group the outcomes by curriculum and filter by those with data
  const groupedOutcomes = useMemo(() => {
    const grouped = outcomes.outcomes.reduce(
      (acc, outcome) => {
        const curriculum = outcome.outcome.curriculum;
        if (!acc[curriculum]) {
          acc[curriculum] = [];
        }
        acc[curriculum].push(outcome);
        return acc;
      },
      {} as Record<string, ActivityOutcomeGroup[]>
    );

    // Only include curriculums that have data
    return grouped;
  }, [outcomes]);

  // Available curriculums (only those with data)
  const availableCurriculums = useMemo(() => {
    const available = Object.keys(groupedOutcomes);
    return curriculumOptions.filter(option => available.includes(option.value));
  }, [curriculumOptions, groupedOutcomes]);

  // Get outcomes for active curriculum
  const activeOutcomes = useMemo(() => {
    if (!activeCurriculum || !groupedOutcomes[activeCurriculum]) {
      return [];
    }

    return groupedOutcomes[activeCurriculum];
  }, [activeCurriculum, groupedOutcomes]);

  // Set initial active curriculum
  useMemo(() => {
    if (availableCurriculums.length > 0 && !activeCurriculum) {
      setActiveCurriculum(availableCurriculums[0].value);
    }
  }, [availableCurriculums, activeCurriculum]);

  return {
    curriculumOptions,
    availableCurriculums,
    activeOutcomes,
    activeCurriculum,
    setActiveCurriculum,
  };
};

export default useTeachingNotesOutcomeDisplay;
